import React from 'react';
import { useTranslation } from 'react-i18next';
import OpArticle, { ArticleSection } from '../components/optimus/OpArticle';

interface ReactPerformanceOptimizationPageProps {
  darkMode: boolean;
}

const CODE_VISUALIZER = `// vite.config.ts
import { visualizer } from 'rollup-plugin-visualizer';

export default defineConfig({
  plugins: [react(), visualizer({ gzipSize: true })],
  build: {
    rollupOptions: {
      output: {
        manualChunks: {
          vendor: ['react', 'react-dom'],
          animations: ['framer-motion'],
          three: ['three', '@react-three/fiber'],
        },
      },
    },
  },
});`;

const CODE_LAZY = `// src/App.tsx
const HomePage = lazy(() => import('./pages/HomePage'));
const ProjectsPage = lazy(() => import('./pages/ProjectsPage'));
const BlogPage = lazy(() => import('./pages/BlogPage'));

<Suspense fallback={<LoadingSpinner />}>
  <Routes>
    <Route path="/" element={<HomePage />} />
    <Route path="/projects" element={<ProjectsPage />} />
    <Route path="/blog" element={<BlogPage />} />
  </Routes>
</Suspense>`;

const ReactPerformanceOptimizationPage: React.FC<ReactPerformanceOptimizationPageProps> = ({
  darkMode,
}) => {
  const { t } = useTranslation();
  const b = 'op.blog.perf';

  const sections: ArticleSection[] = [
    { title: t(`${b}.s1t`), paragraphs: [t(`${b}.s1p1`), t(`${b}.s1p2`)] },
    {
      title: t(`${b}.s2t`),
      paragraphs: [t(`${b}.s2p1`)],
      code: { filename: 'vite.config.ts', content: CODE_VISUALIZER },
    },
    {
      title: t(`${b}.s3t`),
      paragraphs: [t(`${b}.s3p1`)],
      code: { filename: 'src/App.tsx', content: CODE_LAZY },
    },
    { title: t(`${b}.s4t`), paragraphs: [t(`${b}.s4p1`)] },
    { title: t(`${b}.s5t`), paragraphs: [t(`${b}.s5p1`)] },
    {
      title: t(`${b}.s6t`),
      paragraphs: [t(`${b}.s6p1`)],
      stats: [
        { value: '35 → 93', label: t(`${b}.st1`) },
        { value: '68 → 99', label: t(`${b}.st2`) },
        { value: '1.1s', label: t(`${b}.st3`) },
        { value: '45ms', label: t(`${b}.st4`) },
      ],
    },
  ];

  const takeaways = [1, 2, 3, 4].map((n) => ({
    title: t(`${b}.k${n}t`),
    description: t(`${b}.k${n}d`),
  }));

  return (
    <OpArticle
      darkMode={darkMode}
      slug="react-performance-optimization"
      category={t(`${b}.category`)}
      title={t('blogPosts.reactPerformanceOptimization.title')}
      description={t('blogPosts.reactPerformanceOptimization.description')}
      date="2025-06-16"
      readingTime={6}
      tags={['React', 'Vite', 'Lighthouse', 'Performance', 'Code Splitting']}
      image="/LightHouseScores.png"
      seoKeywords="react performance optimization, lighthouse score improvement, web performance, vite optimization, bundle splitting"
      sections={sections}
      takeaways={takeaways}
    />
  );
};

export default ReactPerformanceOptimizationPage;
